/* GRND — pre-launch gate.
   Lives in a real .js asset so no Liquid can ever touch it. Everything it needs
   from the template arrives via data- attributes or the presence of an element.

   The custom cursor and the parallax drift are gone. They ran a
   requestAnimationFrame loop each, forever, for decoration that appears nowhere
   else on the store.

   What is left is the countdown, which is information, and two dialogs that
   need real focus handling: the offer popup and the password prompt. */
(function () {
  var root = document.querySelector('[data-grnd-pw]');
  if (!root) return;

  /* ------------------------------------------------------------------
     Countdown. Defined before anything can bail out — a ticking deadline
     is the page's only live fact.
     ------------------------------------------------------------------ */
  if (!window.customElements.get('grnd-countdown')) {
    window.customElements.define(
      'grnd-countdown',
      class GrndCountdown extends HTMLElement {
        connectedCallback() {
          this.target = new Date(this.dataset.date).getTime();

          /* An unset or malformed date hides the block rather than showing a
             row of dashes forever. */
          if (isNaN(this.target)) {
            this.hidden = true;
            return;
          }

          this.fields = {
            days: this.querySelector('[data-days]'),
            hours: this.querySelector('[data-hours]'),
            minutes: this.querySelector('[data-minutes]'),
            seconds: this.querySelector('[data-seconds]')
          };

          this.hidden = false;
          this.tick();
          this.timer = setInterval(this.tick.bind(this), 1000);
        }

        disconnectedCallback() {
          clearInterval(this.timer);
        }

        tick() {
          var remaining = Math.max(0, this.target - Date.now());
          var total = Math.floor(remaining / 1000);

          this.set('days', Math.floor(total / 86400));
          this.set('hours', Math.floor((total % 86400) / 3600));
          this.set('minutes', Math.floor((total % 3600) / 60));
          this.set('seconds', total % 60);

          if (remaining === 0) {
            clearInterval(this.timer);
            this.classList.add('is-expired');
          }
        }

        set(key, value) {
          var el = this.fields[key];
          if (!el) return;
          var next = String(value).padStart(2, '0');
          /* Only write when it changed, so the browser is not re-laying out
             four nodes every second for three unchanged ones. */
          if (el.textContent !== next) el.textContent = next;
        }
      }
    );
  }

  /* ------------------------------------------------------------------
     Dialogs
     ------------------------------------------------------------------ */
  function createModal(el) {
    if (!el) return null;

    var lastFocused = null;
    var api = {};

    api.isOpen = function () {
      return el.classList.contains('is-open');
    };

    api.open = function () {
      if (api.isOpen()) return;
      lastFocused = document.activeElement;
      el.hidden = false;

      /* Force a reflow so the transition has a start frame, then set the open
         state synchronously. Doing this in requestAnimationFrame would leave a
         transparent full-screen overlay swallowing clicks whenever rAF is
         throttled — a background tab, or a window that is not compositing. */
      void el.offsetWidth;
      el.classList.add('is-open');
      document.body.classList.add('grnd-no-scroll');

      /* The field, not the close button that precedes it in the DOM — opening
         straight into the input is the whole point. */
      var target = el.querySelector('input:not([type=hidden])') || el.querySelector('button');
      if (target) target.focus({ preventScroll: true });
    };

    api.close = function () {
      if (!api.isOpen()) return;
      el.classList.remove('is-open');
      document.body.classList.remove('grnd-no-scroll');
      if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });

      var done = function () {
        el.hidden = true;
        el.removeEventListener('transitionend', done);
      };
      el.addEventListener('transitionend', done);
      /* Guarantee it closes even if no transition fires — reduced motion turns
         them all off. */
      setTimeout(done, 600);
    };

    /* Backdrop */
    el.addEventListener('click', function (event) {
      if (event.target === el) api.close();
    });

    el.querySelectorAll('[data-grnd-close]').forEach(function (button) {
      button.addEventListener('click', api.close);
    });

    /* Focus trap */
    el.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;

      var focusable = el.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden])'
      );
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    return api;
  }

  var offer = createModal(document.querySelector('[data-grnd-modal="offer"]'));
  var lock = createModal(document.querySelector('[data-grnd-modal="lock"]'));

  document.querySelectorAll('[data-grnd-open="offer"]').forEach(function (button) {
    button.addEventListener('click', function () {
      if (offer) offer.open();
    });
  });

  document.querySelectorAll('[data-grnd-open="lock"]').forEach(function (button) {
    button.addEventListener('click', function () {
      if (lock) lock.open();
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    if (offer && offer.isOpen()) offer.close();
    if (lock && lock.isOpen()) lock.close();
  });

  /* Re-open the password prompt on a wrong password. The element's presence is
     the signal — the template renders it only in that case, which keeps this
     file free of any template logic. */
  var lockFailed = !!document.querySelector('[data-grnd-lock-error]');
  if (lockFailed && lock) lock.open();

  /* ------------------------------------------------------------------
     Popup on a timer

     Suppressed in three cases, all of which mean the visitor has already dealt
     with the offer or is busy with something else: the form was submitted, the
     form came back with an error and is showing it inline, or the password
     prompt is up.
     ------------------------------------------------------------------ */
  var delay = parseInt(root.dataset.autoDelay || '0', 10);
  var settled =
    document.querySelector('[data-grnd-offer-posted]') || document.getElementById('GrndOfferError');

  if (delay > 0 && offer && !settled && !lockFailed) {
    var KEY = 'grnd-offer-seen';
    var seen;

    try {
      seen = window.sessionStorage.getItem(KEY);
    } catch (e) {
      /* Private mode throws on access — treat it as not seen. */
      seen = null;
    }

    if (!seen) {
      setTimeout(function () {
        /* Re-checked at fire time, not just at schedule time: the visitor may
           have opened the password prompt during the wait. */
        if (lock && lock.isOpen()) return;
        offer.open();

        try {
          window.sessionStorage.setItem(KEY, '1');
        } catch (e) {
          /* Private mode — just skip persistence. */
        }
      }, delay);
    }
  }
})();
